import {
  EntityTypeEnum,
  InterviewRequestSourceEnum,
  OperationTypeEnum
} from "@rooster/types";
import { useTranslation } from "react-i18next";
import { recordTermsAcceptanceIfApplicable } from "../helpers/meeting/recordTermsAcceptanceIfApplicable";
import {
  scheduleAutobookHelper,
  scheduleRoosterLinkHelper
} from "../helpers/meeting/schedule";
import {
  validateAutobookHelper,
  validateRoosterLinkHelper
} from "../helpers/meeting/validate";
import { useSchedulerStore } from "../state/scheduler.store";
import { BookingFormType, LegalType } from "../types";
import { handleRoosterLinkValidationErrorMessageState } from "../utils/errorManagement";

export const useBookingForm = () => {
  const { t } = useTranslation();
  const {
    sessionData,
    clientId,
    trackingId,
    host,
    providers,
    link,
    meetingLink,
    actions,
    user,
    ui
  } = useSchedulerStore();

  const handleFormInput = async (
    form: BookingFormType,
    legal: LegalType[],
    selectedLanguages: Record<string, string> = {}
  ) => {
    actions.setSessionData({ ...sessionData, formData: form });

    if (!sessionData || !clientId || !host || !providers) return;

    if (link?.scheduleId && meetingLink?.type === "rooster") {
      const isValid = await validateAutobookHelper({
        link,
        clientId,
        host,
        sessionData,
        meetingLink,
        actions,
        providers,
        ui,
        t
      });

      if (!isValid) return;
      const scheduleResult = await scheduleAutobookHelper({
        availabilityId: link?.scheduleId,
        sessionData,
        meetingLink,
        actions
      });
      actions.setCleanSession(false);

      // Sequence T&C audit only on schedule success. Awaited but best-effort:
      // the helper resolves silently on network failure so we never block
      // the success state.
      if (scheduleResult === "OK") {
        await recordTermsAcceptanceIfApplicable({
          clientId,
          requestType: InterviewRequestSourceEnum.AUTOBOOK,
          operationType: sessionData?.isRescheduleFlow
            ? OperationTypeEnum.RESCHEDULE
            : OperationTypeEnum.SCHEDULE,
          entityType: EntityTypeEnum.INTERVIEW,
          // Interview doc id equals the availability id the client passed in;
          // confirmed at server/src/routes/interview/autobook/helpers/common.ts:472
          // which uses db.collection(TABLES.INTERVIEWS).doc(id).
          entityId: link.scheduleId,
          meetingLinkId: meetingLink?.id,
          trackingId: trackingId ?? "",
          legal,
          selectedLanguages
        });
      }
    } else if (meetingLink?.type === "rooster_links") {
      if (meetingLink && providers && sessionData && link && host) {
        const { hasError, isCancelled, isValidatedViaApi, error } =
          await validateRoosterLinkHelper({
            personIdentifier: link.userId ?? "",
            profileIdentifier: link.linkId ?? "",
            userUid: user?.id ?? "",
            meetingLink,
            link
          });

        const isProspectLink =
          meetingLink.roosterLinksMeta?.profileType === "prospect";

        handleRoosterLinkValidationErrorMessageState(
          isValidatedViaApi,
          isCancelled,
          error,
          actions,
          ui,
          host,
          isProspectLink,
          t
        );

        if (hasError) {
          actions.setSessionValidation("validation_failed");
          return;
        }

        const response = await scheduleRoosterLinkHelper({
          meetingLink,
          providers,
          sessionData,
          link,
          host,
          actions,
          formData: form,
          type: sessionData?.isRescheduleFlow ? "reschedule" : "schedule",
          // Same gated list the T&C audit uses — empty for internal users, so
          // the persisted interview consent matches what the UI presented.
          legal,
          ui,
          t
        });

        actions.setCleanSession(false);

        // scheduleRoosterLinkHelper resolves with the full response object
        // even on known failures (e.g. SLOT_NOT_AVAILABLE), so an
        // availabilityId alone is not enough — gate on `scheduled === true`
        // to avoid recording T&C acceptance for a booking that never
        // actually completed.
        if (response?.scheduled === true && response?.availabilityId) {
          const url = new URL(window.location.href);
          url.searchParams.delete("id");
          url.searchParams.append("id", response?.availabilityId);
          window.history.replaceState(null, "", url.toString());

          // Sequence T&C audit only on schedule success. The server's
          // /v2/schedule handler creates BOTH an availability doc and an
          // interview doc with the same id (the returned `availabilityId`
          // is also the interview doc id — see
          // functions/src/personalMeetingLinks.ts:743-796). The candidate's
          // user-perceived action is "I scheduled/rescheduled an
          // interview", so we record entityType: INTERVIEW for consistency
          // with the autobook flow.
          await recordTermsAcceptanceIfApplicable({
            clientId,
            requestType: isProspectLink
              ? InterviewRequestSourceEnum.ROOSTERLINKS_PROSPECT
              : InterviewRequestSourceEnum.ROOSTERLINKS_MEETING,
            operationType: sessionData?.isRescheduleFlow
              ? OperationTypeEnum.RESCHEDULE
              : OperationTypeEnum.SCHEDULE,
            entityType: EntityTypeEnum.INTERVIEW,
            entityId: response.availabilityId,
            meetingLinkId: meetingLink?.id,
            trackingId: trackingId ?? "",
            legal,
            selectedLanguages
          });
        }
      }
    }
  };

  return { handleFormInput };
};

export type UseBookingFormReturnType = ReturnType<typeof useBookingForm>;
